import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ShieldAlert, X, RefreshCcw } from 'lucide-react';
import { log } from '../../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * iter114s.22 — Surfaces the backend's `/api/admin/env-health` report
 * as a high-contrast red banner in the admin dashboard whenever any
 * critical secret has been wiped from `/app/backend/.env` (the
 * recurring pod-rebuild failure mode).
 *
 * Why a banner and not a toast?
 *   * Persistent visibility — admins can close it temporarily, but on
 *     the next page load it comes back if the issue isn't resolved.
 *   * Each missing key gets a one-line "feature → symptom" so the
 *     admin instantly knows what's broken without paging through logs.
 *   * No values, no auth tokens, no PII surface — only key names +
 *     length (so a truncated paste shows up too).
 *
 * Polls every 60s while the dashboard is open so a fresh `.env` paste
 * + backend restart clears the banner without a manual refresh.
 */
export const EnvHealthBanner = () => {
  const [report, setReport] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealth = async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/admin/env-health`, { withCredentials: true });
      setReport(res.data);
    } catch (e) {
      // Silent — non-admins won't see the banner anyway, and a network
      // blip shouldn't crash the dashboard. Logged for ops.
      log.warn('[env-health] fetch failed:', e?.response?.status || e?.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 60_000);
    return () => clearInterval(id);
  }, []);

  if (!report) return null;
  if (report.healthy) return null;
  if (dismissed) return null;

  const missing = report.critical_missing || [];

  return (
    <div
      className="mb-4 rounded-xl border-2 border-red-300 bg-red-50 p-4 shadow-md"
      data-testid="env-health-banner"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-bold text-red-900 text-base">
              {missing.length === 1 ? '1 backend secret is missing' : `${missing.length} backend secrets are missing`}
              <span className="ml-2 text-sm font-normal text-red-700">
                — customer-visible breakage is happening right now.
              </span>
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={fetchHealth}
                disabled={refreshing}
                className="inline-flex items-center gap-1 text-xs font-medium text-red-800 hover:text-red-950 px-2 py-1 rounded hover:bg-red-100 disabled:opacity-50"
                data-testid="env-health-banner-refresh"
                title="Re-check after pasting fresh keys + restarting the backend"
              >
                <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                Re-check
              </button>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                className="text-red-700 hover:text-red-900 p-1 rounded hover:bg-red-100"
                aria-label="Dismiss banner"
                data-testid="env-health-banner-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <ul className="mt-3 space-y-2">
            {missing.map((m) => (
              <li
                key={m.key}
                className="text-sm text-red-900 leading-snug"
                data-testid={`env-health-missing-${m.key.toLowerCase()}`}
              >
                <code className="font-mono font-bold bg-red-100 px-1.5 py-0.5 rounded">
                  {m.key}
                </code>{' '}
                — <span className="font-semibold">{m.feature}</span>.
                <span className="block ml-4 text-red-800 text-xs italic">
                  ↳ {m.symptom}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-3 flex items-start gap-2 text-xs text-red-800">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Paste the missing keys into{' '}
              <code className="font-mono bg-red-100 px-1 rounded">/app/backend/.env</code>{' '}
              and restart the backend (
              <code className="font-mono bg-red-100 px-1 rounded">supervisorctl restart backend</code>
              ). Click "Re-check" above when done — the banner clears as soon as the keys are detected.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvHealthBanner;
